import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import Button from "@material-ui/core/Button";
import {
  decodeId,
  formatMessage,
  fetchCustomFilter,
} from "@openimis/fe-core";
import { withTheme, withStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import AdvancedCriteriaRowValue from "./AdvancedCriteriaRowValue";
import AddCircle from '@material-ui/icons/Add';
import { BENEFIT_PLAN, CLEARED_STATE_FILTER } from "../constants";
import { isEmptyObject } from "../utils";
import {
  applyCriteriaRowsToPaymentPlan,
  buildSavedCriteriaRows,
  criteriaRowsEqual,
  getAdvancedCriteriaList,
  mergeAdvancedCriteriaIntoJsonExt,
  mergeSavedAndDraftCriteriaRows,
} from "../utils/advancedCriteriaJsonExt";

const styles = (theme) => ({
  item: theme.paper.item,
});

const resolveBenefitPlanObjectId = (benefitPlan) => {
  if (!benefitPlan?.id) return null;
  const { id } = benefitPlan;
  if (/^\d+$/.test(id)) return id;
  try {
    return decodeId(id);
  } catch {
    return id;
  }
};

const AdvancedCriteriaDialog = ({
  intl,
  classes,
  object,
  objectToSave,
  fetchCustomFilter,
  customFilters,
  moduleName,
  objectType,
  setAppliedCustomFilters,
  setAppliedFiltersRowStructure,
  updateAttributes,
  getDefaultAppliedCustomFilters,
  additionalParams,
  confirmed,
  readOnly = false,
  registerCriteriaFlush,
}) => {
  const [currentFilter, setCurrentFilter] = useState({
    field: "", filter: "", type: "", value: "", amount: "", referential: null, typeLocation: null,
  });
  const [filters, setFilters] = useState(() => getDefaultAppliedCustomFilters());
  const skipNextSyncRef = useRef(false);
  const pendingJsonExtSyncRef = useRef(false);
  const filtersRef = useRef(filters);
  const objectToSaveRef = useRef(objectToSave);

  filtersRef.current = filters;
  objectToSaveRef.current = objectToSave;

  useEffect(() => {
    if (!registerCriteriaFlush) {
      return undefined;
    }

    registerCriteriaFlush((paymentPlan) => (
      applyCriteriaRowsToPaymentPlan(paymentPlan ?? objectToSaveRef.current, filtersRef.current)
    ));

    return () => registerCriteriaFlush(null);
  }, [registerCriteriaFlush, filters, objectToSave?.jsonExt]);

  useEffect(() => {
    if (pendingJsonExtSyncRef.current) {
      pendingJsonExtSyncRef.current = false;
      return;
    }
    const parsed = getDefaultAppliedCustomFilters(objectToSave?.jsonExt);
    skipNextSyncRef.current = true;
    setFilters((current) => mergeSavedAndDraftCriteriaRows(parsed, current));
  }, [objectToSave?.jsonExt]);

  useLayoutEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }
    if (!object || isEmptyObject(object)) {
      return;
    }

    const savedRows = buildSavedCriteriaRows(filters);
    const currentRows = getAdvancedCriteriaList(objectToSave?.jsonExt);
    if (criteriaRowsEqual(savedRows, currentRows)) {
      return;
    }

    const jsonExt = mergeAdvancedCriteriaIntoJsonExt(objectToSave?.jsonExt, savedRows);
    pendingJsonExtSyncRef.current = true;
    updateAttributes(jsonExt);
    setAppliedFiltersRowStructure(savedRows);
    setAppliedCustomFilters(JSON.stringify(savedRows));
  }, [filters, object]);

  const createParams = (moduleName, objectTypeName, uuidOfObject = null, additionalParams = null) => {
    const params = [
      `moduleName: "${moduleName}"`,
      `objectTypeName: "${objectTypeName}"`,
    ];
    if (uuidOfObject) {
      params.push(`uuidOfObject: "${uuidOfObject}"`);
    }
    if (additionalParams) {
      params.push(`additionalParams: ${JSON.stringify(JSON.stringify(additionalParams))}`);
    }
    return params;
  };

  const fetchFilters = (params) => fetchCustomFilter(params);

  const handleAddFilter = () => {
    setCurrentFilter(CLEARED_STATE_FILTER);
    setFilters([...filters, CLEARED_STATE_FILTER]);
  };

  const handleRemoveFilter = () => {
    setCurrentFilter(CLEARED_STATE_FILTER);
    setAppliedFiltersRowStructure([]);
    setFilters([]);
    const clearedJsonExt = mergeAdvancedCriteriaIntoJsonExt(objectToSave?.jsonExt, []);
    updateAttributes(clearedJsonExt);
    setAppliedCustomFilters(JSON.stringify([]));
  };

  useEffect(() => {
    if (object && isEmptyObject(object) === false) {
      let paramsToFetchFilters = [];
      if (objectType === BENEFIT_PLAN) {
        paramsToFetchFilters = createParams(
          moduleName,
          objectType,
          resolveBenefitPlanObjectId(object),
          additionalParams,
        );
      } else {
        paramsToFetchFilters = createParams(
          moduleName,
          objectType,
          additionalParams,
        );
      }
      fetchFilters(paramsToFetchFilters);
    }
  }, [object]);

  const benefitPlanId = resolveBenefitPlanObjectId(object);

  return (
    <>
      {filters.map((filter, index) => (
        <AdvancedCriteriaRowValue
          key={`criteria-row-${index}`}
          customFilters={customFilters}
          currentFilter={filter}
          setCurrentFilter={setCurrentFilter}
          index={index}
          filters={filters}
          setFilters={setFilters}
          readOnly={confirmed || readOnly}
          benefitPlanId={benefitPlanId}
        />
      ))}
      {!confirmed ? (
        <div style={{ backgroundColor: "#DFEDEF", paddingLeft: "10px", paddingBottom: "10px" }}>
          <AddCircle
            style={{
              border: 'thin solid',
              borderRadius: '40px',
              width: '16px',
              height: '16px',
              cursor: 'pointer',
            }}
            onClick={handleAddFilter}
          />
          <Button
            onClick={handleAddFilter}
            variant="outlined"
            style={{
              border: "0px",
              marginBottom: "6px",
              fontSize: "0.8rem",
            }}
            disabled={confirmed || readOnly}
          >
            {formatMessage(intl, "paymentPlan", "paymentPlan.advancedCriteria.button.addFilters")}
          </Button>
        </div>
      ) : null}
      <div>
        {filters.length > 0 && (
          <div style={{ float: 'left' }}>
            <Button
              onClick={handleRemoveFilter}
              variant="outlined"
              style={{ border: '0px' }}
              disabled={confirmed || readOnly}
            >
              {formatMessage(intl, 'individual', 'paymentPlan.advancedCriteria.button.clearAllFilters')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  rights: !!state.core && !!state.core.user && !!state.core.user.i_user ? state.core.user.i_user.rights : [],
  confirmed: state.core.confirmed,
  fetchingCustomFilters: state.core.fetchingCustomFilters,
  errorCustomFilters: state.core.errorCustomFilters,
  fetchedCustomFilters: state.core.fetchedCustomFilters,
  customFilters: state.core.customFilters,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchCustomFilter,
}, dispatch);

export default injectIntl(withTheme(withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(AdvancedCriteriaDialog))));
